import { useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import { AjaxContext, AppContext, EMPTY_ARRAY, UserContext, ERROR_SEVERITY, formatListData } from 'commons-ui';
import { AthenaCrudRequestRecord, SelectedAthenaCrudType } from './listAthenaRequest/types';
import { EAthenaDashboardURL } from './helper';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const location = useLocation();
	const {
		app: { appName }
	} = useContext(AppContext);
	const { user } = useContext(UserContext);
	const getErrorMessage = (error: any): string => {
		try {
			let firstIndex: number = error.response.data.error.indexOf('-');
			const errorObject = JSON.parse(error.response.data.error.slice(firstIndex + 1).trim()).error;
			firstIndex = errorObject.indexOf('-');
			const errorMessage: string = JSON.stringify(JSON.parse(errorObject.slice(firstIndex + 1).trim())?.data);
			return errorMessage;
		} catch (e: any) {
			return 'Something went wrong';
		}
	};

	const getAthenaCrudRequests = useCallback(
		async ({
			pageDetails
		}: {
			pageDetails: { pageNo: number; pageSize: number };
		}): Promise<{
			rowCount: number;
			data: AthenaCrudRequestRecord[];
		}> => {
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const result = await postData({
				endpoint: EAthenaDashboardURL.LIST_ATHENA_CRUD_REQUEST,
				queryParams: {
					pageNumber: pageNo,
					limit: pageSize,
					skip: skipCount,
					appName
				},
				body: {
					pageNumber: pageNo,
					limit: pageSize
				}
			});
			return {
				rowCount: _get(result, ['data', 'count'], 0),
				data: formatListData(_get(result, ['data', 'data'], EMPTY_ARRAY))
			};
		},
		EMPTY_ARRAY
	);
	const approveAthenaCrudRequest = useCallback(async (submittedData: AthenaCrudRequestRecord, showAlert: any): Promise<void> => {
		try {
			const result = await postData({
				endpoint: EAthenaDashboardURL.APPROVE_AND_PROCESS_ATHENA_CRUD_REQUEST,
				queryParams: {
					appName
				},
				body: {
					id: submittedData._id,
					approverName: user?.userName,
					approverId: user?.email
				}
			});
			if (result.data.success !== true) {
				showAlert(result.data.externalMessage || result.data.error || 'Error occurred while approving request', {
					type: ERROR_SEVERITY.ERROR
				});
				return;
			}
			showAlert('Approved successfully and request is being processed');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while approving request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const rejectAthenaCrudRequest = useCallback(async (submittedData: AthenaCrudRequestRecord, showAlert: any): Promise<void> => {
		try {
			const result = await postData({
				endpoint: EAthenaDashboardURL.REJECT_ATHENA_CRUD_REQUEST,
				queryParams: {
					appName
				},
				body: {
					id: submittedData._id,
					rejectedBy: user?.userName,
					rejectedId: user?.email
				}
			});
			if (result.data.success !== true) {
				showAlert(result.data.externalMessage || result.data.error || 'Error occurred while rejecting request', {
					type: ERROR_SEVERITY.ERROR
				});
				return;
			}
			showAlert('Rejected successfully');
		} catch (error: any) {
			showAlert(getErrorMessage(error) || 'Error occurred while rejecting request', { type: ERROR_SEVERITY.ERROR });
		}
	}, EMPTY_ARRAY);

	const createAthenaCrudRequest = useCallback(
		async (gameName: string, queryText: string, defaultQueryParams: string, label: string, showAlert: any): Promise<boolean> => {
			try {
				const response = await postData({
					endpoint: EAthenaDashboardURL.CREATE_ATHENA_CRUD_REQUEST,
					queryParams: {
						appName
					},
					body: {
						gameName,
						queryText,
						defaultQueryParams: JSON.parse(defaultQueryParams),
						label
					}
				});
				if (response.data.success !== true) {
					showAlert(response.data.externalMessage || response.data.error || 'Error occurred while creating request', {
						type: ERROR_SEVERITY.ERROR
					});
					return false;
				}
				showAlert('created successfully and request is being processed');
			} catch (error: any) {
				showAlert(getErrorMessage(error) || 'Error occurred while creating request', { type: ERROR_SEVERITY.ERROR });
				return false;
			}
			return true;
		},
		EMPTY_ARRAY
	);

	const updateAthenaCrudRequest = useCallback(
		async (id: string, data: SelectedAthenaCrudType, showAlert: any): Promise<boolean> => {
			try {
				const result = await postData({
					endpoint: EAthenaDashboardURL.UPDATE_ATHENA_CRUD_REQUEST,
					queryParams: {
						appName
					},
					body: {
						id,
						gameName: data.gameName,
						queryText: data.queryText,
						defaultQueryParams: JSON.parse(data.defaultQueryParams),
						label: data.label
					}
				});
				if (result.data.success !== true) {
					showAlert(result.data.externalMessage || result.data.error || 'Error occurred while updating request', {
						type: ERROR_SEVERITY.ERROR
					});
					return false;
				}
				showAlert('Updated successfully and request is being processed');
			} catch (error: any) {
				showAlert(getErrorMessage(error) || 'Error occurred while updating request', { type: ERROR_SEVERITY.ERROR });
				return false;
			}
			return true;
		},
		EMPTY_ARRAY
	);

	const getAthenaCrudRequestById = useCallback(async (id: string): Promise<AthenaCrudRequestRecord> => {
		const result = await postData({
			endpoint: EAthenaDashboardURL.GET_ATHENA_CRUD_REQUEST_BY_ID,
			queryParams: {
				appName
			},
			body: {
				id
			}
		});
		return result.data.data;
	}, EMPTY_ARRAY);

	const getAthenaRequestsVersionHistory = useCallback(
		async ({
			pageDetails
		}: {
			pageDetails: { pageNo: number; pageSize: number };
		}): Promise<{
			rowCount: number;
			data: AthenaCrudRequestRecord[];
		}> => {
			const data = _get(location, ['state', 'rowData']);
			const pageNo: number = pageDetails.pageNo || 1;
			const pageSize: number = pageDetails.pageSize || 20;
			const result = await postData({
				endpoint: EAthenaDashboardURL.GET_ATHENA_CRUD_REQUEST_VERSION_HISTORY,
				queryParams: {
					appName
				},
				body: {
					requestId: data._id,
					pageNumber: pageNo,
					limit: pageSize
				}
			});
			return {
				rowCount: _get(result, ['data', 'count'], 0),
				data: formatListData(_get(result, ['data', 'data'], EMPTY_ARRAY))
			};
		},
		EMPTY_ARRAY
	);

	return {
		getAthenaCrudRequests,
		createAthenaCrudRequest,
		approveAthenaCrudRequest,
		rejectAthenaCrudRequest,
		getAthenaCrudRequestById,
		updateAthenaCrudRequest,
		getAthenaRequestsVersionHistory
	};
};

export default useService;
